import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type { CustomerMatchPageOut, InvoicePageOut, OutletRevenueOut, StaffOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { useAuth } from '../auth/AuthContext'
import { Pager } from '../components/Pager'
import { Panel } from '../components/States'
import { StatusChip } from '../components/StatusChip'
import { usePaging } from '../lib/filters'
import { branchName, count, formatDate, fullName, humanise, money } from '../lib/format'

const STATUSES = [
  'pending_fulfillment_assignment',
  'pending_approval',
  'approved',
  'rejected',
  'refunded',
  'layby',
  'parked',
  'historical',
]

const SOURCES = ['hike', 'shopify', 'cash_bill']

/** What the search box matches: the invoice number, or the customer's name or phone. */
const SEARCH_BY = [
  {
    key: 'invoice',
    label: 'Invoice #',
    field: 'Invoice or reference number',
    placeholder: 'e.g. 00019975',
  },
  {
    key: 'customer',
    label: 'Customer',
    field: 'Customer name or phone',
    placeholder: 'e.g. Kirk Grant or 876-550-8006',
  },
] as const
type SearchBy = (typeof SEARCH_BY)[number]['key']

export function InvoicesPage() {
  // Coordinators use this page as the delivery lookup. The branch and
  // salesperson filters are fed by admin-only endpoints, so they are hidden.
  const { isAdmin } = useAuth()
  const [params, setParams] = useSearchParams()
  const { offset, limit, setOffset } = usePaging(50)

  const search = params.get('q') ?? ''
  const searchBy: SearchBy = params.get('by') === 'customer' ? 'customer' : 'invoice'
  const mode = SEARCH_BY.find((option) => option.key === searchBy)!
  // Customer search is two steps: a partial name or phone can match many
  // people, so the matching customers are listed first, and picking one
  // (customer_key in the URL) filters the invoices to them.
  const customerKey = searchBy === 'customer' ? params.get('customer') ?? '' : ''
  // The API rejects a 1-character customer search; wait for a second one.
  const customerQuery = searchBy === 'customer' && search.trim().length >= 2 ? search : ''
  const pickingCustomer = customerQuery !== '' && customerKey === ''
  const status = params.get('status') ?? ''
  const source = params.get('source') ?? ''
  const salesPerson = params.get('sales_person') ?? ''
  const outlet = params.get('outlet') ?? ''

  // The text box is local so typing stays responsive; the URL — and the query —
  // only update once typing pauses.
  const [draft, setDraft] = useState(search)
  useEffect(() => setDraft(search), [search])

  useEffect(() => {
    if (draft === search) return
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params)
      if (draft) next.set('q', draft)
      else next.delete('q')
      // New words, new customers: whoever was picked no longer applies.
      next.delete('customer')
      next.delete('offset')
      setParams(next, { replace: true })
    }, 350)
    return () => clearTimeout(timer)
  }, [draft, search, params, setParams])

  function setSearchBy(next: SearchBy) {
    const updated = new URLSearchParams(params)
    if (next === 'invoice') updated.delete('by')
    else updated.set('by', next)
    updated.delete('customer')
    updated.delete('offset')
    setParams(updated, { replace: true })
  }

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    next.delete('offset')
    setParams(next, { replace: true })
  }

  const outlets = useQuery({
    queryKey: ['outlets'],
    enabled: isAdmin,
    staleTime: 60 * 60 * 1000,
    queryFn: () =>
      api.get<OutletRevenueOut>('/analytics/revenue/by-outlet', {
        from_date: '2020-01-01',
        to_date: new Date().toISOString().slice(0, 10),
        compare: 'none',
        scope: 'all',
      }),
  })

  const staff = useQuery({
    queryKey: ['staff-manage'],
    enabled: isAdmin,
    staleTime: 10 * 60 * 1000,
    queryFn: () => api.get<StaffOut[]>('/staff/manage'),
  })

  const customers = useQuery({
    queryKey: ['invoice-customers', customerQuery],
    enabled: customerQuery !== '',
    queryFn: () =>
      api.get<CustomerMatchPageOut>('/invoices/customers', {
        q: customerQuery,
        include_inactive: true,
        limit: 50,
      }),
  })
  const chosen = customers.data?.items.find((match) => match.customer_key === customerKey)

  const query = useQuery({
    queryKey: ['invoices', searchBy, search, customerKey, status, source, salesPerson, outlet, offset, limit],
    enabled: !pickingCustomer,
    queryFn: () =>
      api.get<InvoicePageOut>('/invoices', {
        invoice_number: searchBy === 'invoice' ? search || null : null,
        customer_key: customerKey || null,
        status: status || null,
        source: source || null,
        sales_person_id: salesPerson || null,
        outlet_id: outlet || null,
        // A search should find parked (unpaid) sales too; browsing shows
        // sales, with parked ones a status filter away.
        include_inactive: search || customerKey ? true : null,
        limit,
        offset,
      }),
  })

  const rows = query.data?.items ?? []
  const salesPeople = (staff.data ?? []).filter((person) => person.role === 'sales')

  return (
    <>
      <PageHeader
        title={isAdmin ? 'Invoices' : 'Delivery lookup'}
        subtitle={
          isAdmin
            ? 'Search by invoice number or by customer name or phone, and filter by branch, status, source or salesperson'
            : 'Find an invoice by number or by customer name or phone, then open it for proof of delivery'
        }
      />

      <div className="content stack">
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="seg" role="group" aria-label="Search by">
            {SEARCH_BY.map((option) => (
              <button
                key={option.key}
                className={searchBy === option.key ? 'on' : ''}
                aria-pressed={searchBy === option.key}
                onClick={() => setSearchBy(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="field" style={{ flex: 1, minWidth: 240 }}>
            <label htmlFor="q">{mode.field}</label>
            <input
              id="q"
              type="search"
              placeholder={mode.placeholder}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="status">Status</label>
            <select id="status" value={status} onChange={(event) => setFilter('status', event.target.value)}>
              <option value="">Any status</option>
              {STATUSES.map((option) => (
                <option key={option} value={option}>
                  {humanise(option)}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="source">Source</label>
            <select id="source" value={source} onChange={(event) => setFilter('source', event.target.value)}>
              <option value="">Any source</option>
              {SOURCES.map((option) => (
                <option key={option} value={option}>
                  {humanise(option)}
                </option>
              ))}
            </select>
          </div>

          {isAdmin ? (
          <>
          <div className="field">
            <label htmlFor="branch">Branch</label>
            <select
              id="branch"
              value={outlet}
              onChange={(event) => setFilter('outlet', event.target.value)}
            >
              <option value="">All branches</option>
              {(outlets.data?.items ?? [])
                .filter((row) => row.outlet_id !== null && row.outlet_name)
                .map((row) => (
                  <option key={row.outlet_id} value={row.outlet_id!}>
                    {row.outlet_name}
                  </option>
                ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="sales_person">Salesperson</label>
            <select
              id="sales_person"
              value={salesPerson}
              onChange={(event) => setFilter('sales_person', event.target.value)}
            >
              <option value="">Anyone</option>
              {salesPeople.map((person) => (
                <option key={person.id} value={person.id}>
                  {fullName(person.first_name, person.last_name)}
                </option>
              ))}
            </select>
          </div>
          </>
          ) : null}

          {search || status || source || salesPerson || outlet ? (
            <button className="btn ghost" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Clear
            </button>
          ) : null}
        </div>

        {pickingCustomer ? (
          <div className="card">
            <div className="card-head">
              <h2>Matching customers</h2>
              <span className="hint">
                {count(customers.data?.total)} customers · pick one to see their invoices
              </span>
            </div>
            <div className="card-body flush">
              <Panel
                isLoading={customers.isPending}
                error={customers.error}
                isEmpty={(customers.data?.items ?? []).length === 0}
                emptyText="No customers matched."
              >
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Phone</th>
                        <th className="num">Invoices</th>
                        <th className="num">Last purchase</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(customers.data?.items ?? []).map((match) => (
                        <tr
                          key={match.customer_key}
                          className="clickable"
                          tabIndex={0}
                          onClick={() => setFilter('customer', match.customer_key)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') setFilter('customer', match.customer_key)
                          }}
                        >
                          <td className="strong">{fullName(match.first_name, match.last_name)}</td>
                          <td className="cell-sub">{match.phone || '—'}</td>
                          <td className="num">{count(match.invoice_count)}</td>
                          <td className="num cell-sub">{formatDate(match.last_invoice_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(customers.data?.total ?? 0) > (customers.data?.items.length ?? 0) ? (
                  <p className="hint" style={{ padding: '10px 16px' }}>
                    Showing the {customers.data?.items.length} most recent — add more of the name or
                    phone to narrow it down.
                  </p>
                ) : null}
              </Panel>
            </div>
          </div>
        ) : (
          <>
            {customerKey ? (
              <div className="row" style={{ alignItems: 'center', gap: 8 }}>
                <span className="chip brand">
                  Customer:{' '}
                  {chosen
                    ? fullName(chosen.first_name, chosen.last_name)
                    : rows[0]
                      ? fullName(rows[0].customer_first_name, rows[0].customer_last_name)
                      : '…'}
                  {chosen?.phone ? ` · ${chosen.phone}` : ''}
                </span>
                <button className="btn ghost sm" onClick={() => setFilter('customer', '')}>
                  ← All matching customers
                </button>
              </div>
            ) : null}
          <div className="card">
            <div className="card-head">
              <h2>Results</h2>
              <span className="hint">{count(query.data?.total)} invoices</span>
            </div>
            <div className="card-body flush">
              <Panel
                isLoading={query.isPending}
                error={query.error}
                isEmpty={rows.length === 0}
                emptyText="No invoices matched."
              >
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th className="num">Date</th>
                        <th>Branch</th>
                        <th>Customer</th>
                        <th>Salesperson</th>
                        <th>Status</th>
                        <th>Fulfilment</th>
                        <th className="num">Total</th>
                        <th className="num">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((invoice) => (
                        <tr key={invoice.id}>
                          <td>
                            <Link to={`/invoices/${invoice.id}`}>
                              {invoice.hike_invoice_number ?? invoice.reference}
                            </Link>
                            <div className="cell-sub">{humanise(invoice.source)}</div>
                          </td>
                          <td className="num cell-sub">
                            {formatDate(invoice.finalize_date ?? invoice.created_at)}
                          </td>
                          <td className="cell-sub" style={{ whiteSpace: 'nowrap' }}>
                            {branchName(invoice.outlet_name)}
                          </td>
                          <td>
                            {isAdmin ? (
                              <Link to={`/customers/${invoice.customer_id}`}>
                                {fullName(invoice.customer_first_name, invoice.customer_last_name)}
                              </Link>
                            ) : (
                              fullName(invoice.customer_first_name, invoice.customer_last_name)
                            )}
                            {invoice.customer_phone ? (
                              <div className="cell-sub">{invoice.customer_phone}</div>
                            ) : null}
                          </td>
                          <td className="cell-sub">
                            {invoice.sales_person_first_name
                              ? fullName(invoice.sales_person_first_name, invoice.sales_person_last_name)
                              : '—'}
                          </td>
                          <td>
                            <StatusChip status={invoice.status} />
                          </td>
                          <td>
                            <StatusChip status={invoice.fulfillment_status} />
                          </td>
                          <td className="num strong">{money(invoice.grand_total)}</td>
                          <td className="num">{money(invoice.total_paid)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager offset={offset} limit={limit} total={query.data?.total ?? 0} onChange={setOffset} />
              </Panel>
            </div>
          </div>
          </>
        )}
      </div>
    </>
  )
}
